// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Label,
    makeStyles,
    Persona,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    SelectTabEventHandler,
    shorthands,
    Tab,
    TabList,
    TabValue,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { Edit24Filled, EditRegular, Info24Regular, Map16Regular, Person16Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { removeAlertsWithMetadata } from '../../redux/features/app/appSlice';
import { AlertMetadata, FeatureKeys, IConversationDisplayMode } from '../../redux/features/app/AppState';
import { ChatScope } from '../../redux/features/conversations/ChatState';
import { Breakpoints } from '../../styles';
import { Alerts } from '../shared/Alerts';
import { ChatRoom } from './ChatRoom';
import { ParticipantsList } from './controls/ParticipantsList';
import { ShareBotMenu } from './controls/ShareBotMenu';
import { EditChatName } from './shared/EditChatName';
import { EditChatScope } from './shared/EditChatScope';
import { ArchiveSourcesTab } from './tabs/ArchiveSourcesTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { PersonaTab } from './tabs/PersonaTab';
import { PlansTab } from './tabs/PlansTab';
import { ChatList } from './chat-list/ChatList';
import useScreenSize from '../../libs/hooks/useScreenSize';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'rgb(0 0 0 / 25%) 0 0.2rem 0.4rem -0.075rem',
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: tokens.colorNeutralBackground4,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        flexWrap: 'wrap',
    },
    chatList: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
        }),
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
    },
    popoverHeader: {
        ...shorthands.margin('0'),
        paddingBottom: tokens.spacingVerticalXXS,
        fontStyle: 'normal',
        fontWeight: '600',
    },
    popover: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ...shorthands.padding(tokens.spacingVerticalXXL),
        ...shorthands.gap(tokens.spacingVerticalMNudge),
        width: '398px',
    },
    input: {
        width: '100%',
    },
    buttons: {
        display: 'flex',
        alignSelf: 'end',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    alerts: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, '72px'),
    },
    scopes: {
        display: 'flex',
        alignItems: 'flex-end',
        marginLeft: '0',
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: '0',
        justifyContent: 'flex-end',
        ...Breakpoints.xSmall({
            flexBasis: '100%',
        }),
        '@media screen and (min-width: 1060px)': { marginLeft: 'auto' },
    },
    centerText: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
});

export const ChatWindow: React.FC = () => {
    const classes = useClasses();
    const { features, conversationDisplayMode, archiveSourceAvailability, alerts } = useAppSelector(
        (state: RootState) => state.app,
    );

    const dispatch = useAppDispatch();
    const screenSize = useScreenSize();

    const showShareBotMenu = features[FeatureKeys.BotAsDocs].enabled || features[FeatureKeys.MultiUserChat].enabled;

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);

    const chatState = selectedId ? conversations[selectedId] : null;

    let chatName = '';
    if (selectedId) {
        chatName = conversations[selectedId].title;
    }

    let conversationHidden = false;

    if (selectedId && conversationDisplayMode === IConversationDisplayMode.Hidden) {
        conversationHidden = true;
    }

    if (selectedId && conversations[selectedId].isHidden) {
        conversationHidden = true;
    }

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = React.useState<TabValue>('chat');

    const onTabSelect: SelectTabEventHandler = (_event, data) => {
        setSelectedTab(data.value);
    };

    React.useEffect(() => {
        setSelectedTab('chat' as TabValue);
        dispatch(removeAlertsWithMetadata(AlertMetadata.NoArchiveSourcesSelected));
    }, [selectedId, dispatch]);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let chatScope: ChatScope = ChatScope.None;
    if (selectedId && conversations[selectedId].chatScope) {
        chatScope = conversations[selectedId].chatScope as ChatScope;
    }

    React.useEffect(() => {
        setSelectedTab('chat' as TabValue);
    }, [chatScope]);

    const selectedSourcesCount =
        (chatState?.selectedSources && chatState.selectedSources.length > 0) ??
        (chatState?.selectedSources &&
            alerts.filter((a) => a.metadata === AlertMetadata.NoArchiveSourcesSelected).length !== 0)
            ? chatState.selectedSources.length
            : archiveSourceAvailability?.Sources.length;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>
                    {!features[FeatureKeys.SimplifiedExperience].enabled && (
                        <>
                            <Persona
                                key={'Semantic Kernel Bot'}
                                size="medium"
                                avatar={{ image: { src: conversations[selectedId].botProfilePicture } }}
                                presence={{ status: 'available' }}
                            />
                            <Label size="large" weight="semibold">
                                {chatName}
                            </Label>
                            <Popover open={isEditing}>
                                <PopoverTrigger disableButtonEnhancement>
                                    <Tooltip content={'Edit conversation name'} relationship="label">
                                        <Button
                                            data-testid="editChatTitleButton"
                                            icon={isEditing ? <Edit24Filled /> : <EditRegular />}
                                            appearance="transparent"
                                            onClick={() => {
                                                setIsEditing(true);
                                            }}
                                            disabled={!chatName}
                                            aria-label="Edit conversation name"
                                        />
                                    </Tooltip>
                                </PopoverTrigger>
                                <PopoverSurface className={classes.popover}>
                                    <h3 className={classes.popoverHeader}>Bot name</h3>
                                    <EditChatName
                                        name={chatName}
                                        chatId={selectedId}
                                        exitEdits={() => {
                                            setIsEditing(false);
                                        }}
                                        textButtons
                                    />
                                </PopoverSurface>
                            </Popover>
                        </>
                    )}
                    {screenSize.width <= 744 && (
                        <div className={classes.chatList}>
                            <ChatList />
                        </div>
                    )}
                    <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
                        <Tab data-testid="chatTab" id="chat" value="chat">
                            Chat
                        </Tab>
                        {chatScope !== ChatScope.Archive && (
                            <Tab data-testid="documentsTab" id="documents" value="documents">
                                Documents
                            </Tab>
                        )}
                        {chatScope === ChatScope.Archive && (
                            <Tab data-testid="archiveSourcesTab" id="archive" value="archive">
                                Archive Sources
                                {archiveSourceAvailability && archiveSourceAvailability.loaded && (
                                    <span>
                                        &nbsp;(
                                        {selectedSourcesCount} source{(selectedSourcesCount ?? 0) > 1 ? 's' : ''}{' '}
                                        selected)
                                    </span>
                                )}
                            </Tab>
                        )}
                        {features[FeatureKeys.PluginsPlannersAndPersonas].enabled && (
                            <>
                                <Tab data-testid="plansTab" id="plans" value="plans" icon={<Map16Regular />}>
                                    Plans
                                </Tab>
                                <Tab data-testid="personaTab" id="persona" value="persona" icon={<Person16Regular />}>
                                    Persona
                                </Tab>
                            </>
                        )}
                    </TabList>
                    <div className={classes.scopes}>
                        <EditChatScope chatId={selectedId} chatScope={chatScope} hiddenChat={conversationHidden} />
                    </div>
                </div>
                <div className={classes.controls}>
                    {!features[FeatureKeys.SimplifiedExperience].enabled && (
                        <div data-testid="chatParticipantsView">
                            <ParticipantsList participants={conversations[selectedId].users} />
                        </div>
                    )}
                    {showShareBotMenu && (
                        <div>
                            <ShareBotMenu chatId={selectedId} chatTitle={chatName} />
                        </div>
                    )}
                </div>
            </div>
            {!selectedId ? (
                <div className={classes.centerText}>
                    <Info24Regular />
                    <Label size="large" weight="semibold">
                        No conversations selected
                    </Label>
                </div>
            ) : (
                <>
                    {selectedTab === 'chat' && <ChatRoom hiddenChat={conversationHidden} />}
                    {selectedTab === 'documents' && <DocumentsTab hiddenChat={conversationHidden} />}
                    {selectedTab === 'archive' && <ArchiveSourcesTab />}
                    {selectedTab === 'plans' && <PlansTab />}
                    {selectedTab === 'persona' && <PersonaTab />}
                    {selectedTab !== 'chat' && selectedTab !== 'archive' && (
                        <div className={classes.alerts}>
                            <Alerts />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
